import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceBase, OrigamiResponse, OrigamiHttpResponse } from './base/service.base';
import { TopicField } from '../utils/topic-utils';
import { InboxEmail as IE } from '@app/origami-api/constants/inbox-email';
import { FolderManagementServiceModel } from '@app/origami-api/services/folder-management.service';
import { ErrorCode } from '../constants/error-codes';

export namespace EmailServiceOptions {
  export interface GetEmailsByView {
    limit?: number;
    siteId: number;
    start?: number;
    viewFilter: string;
    viewMode: number;
    exportParam?: {
      ExportFormat: number | string;
    };
  }

  export interface ExportEmails {
    orderBy: string;
    search?: string;
    siteId: number;
    viewFilter: string;
    viewMode: number;
  }

  export interface DeleteInboxEmail {
    TopicId: number;
    TopicTypeId: number;
  }

  export interface ChangeInboxEmailStatus {
    siteId: number;
    inboxEmailTopicId: number;
    status: IE.ViewFilterByStatus;
    documents?: FolderManagementServiceModel.Document[];
  }
}

export namespace EmailServiceModel {
  export interface EmailView {
    ViewFilter: string;
    ViewFilterDisplayName: string;
    ViewMode: number;
  }

  export interface Emails {
    Messages: Email[];
    TotalRowCount: number;
  }

  export interface Email {
    Attachments: Attachment[];
    Body: string;
    Cc: string;
    From: string;
    MessageId: number;
    SentDate: string;
    SiteId: number;
    Subject: string;
    To: string;
  }

  export interface InboxEmails {
    InboxEmails: InboxEmail[];
    TotalRowCount: number;
  }

  export interface InboxEmail {
    Attachments: Attachment[];
    CreatedBy: number;
    CreatedDate: string;
    LockedBy: any; // TODO: don't known type
    ParentId: number;
    SiteId: number;
    TopicId: number;
    TopicName: string;
    TopicTypeId: number;
    TopicFields: TopicField[];
  }

  export interface Attachment {
    AttachmentId: number;
    FileName: string;
    MessageId: number;
  }
}

export enum emailViewMode {
  byDate = 1,
  byType = 2,
  byMessageIds = 3,
}

@Injectable()
export class EMailService extends ServiceBase {
  serviceUrl = 'EMailService.svc';

  getEmailsViews(
    siteId: number,
    viewMode: number,
    ...params
  ): Observable<OrigamiResponse<EmailServiceModel.EmailView[]>> {
    const body = {
      siteId,
      viewMode,
    };
    return this.origamiPost('/GetEmailsViews', body, ...params);
  }

  getEmailsByView(
    body: EmailServiceOptions.GetEmailsByView,
    ...params
  ): Observable<OrigamiResponse<EmailServiceModel.Emails>> {
    return this.origamiPost('/GetEmailsByView', body, ...params);
  }

  getMessage(messageId: number, siteId: number, ...params): Observable<OrigamiResponse<EmailServiceModel.Email>> {
    const body = {
      messageId,
      siteId,
    };
    return this.origamiPost('/GetMessage', body, ...params);
  }

  getInboxEmailsViews(
    siteId: number,
    viewMode: number,
    ...params
  ): Observable<OrigamiResponse<EmailServiceModel.EmailView[]>> {
    const body = {
      siteId,
      viewMode,
    };
    return this.origamiPost('/GetInboxEmailsViews', body, ...params);
  }

  getInboxEmailsByView(
    body: EmailServiceOptions.GetEmailsByView,
    isForceRequest?: boolean,
  ): Observable<OrigamiResponse<EmailServiceModel.InboxEmails>> {
    return this.origamiPost('/GetInboxEmailsByView', body, null, null, isForceRequest);
  }

  deleteInboxEmails(siteId: number, topics: EmailServiceOptions.DeleteInboxEmail[]): Observable<OrigamiHttpResponse> {
    const body = {
      siteId,
      topics,
    };
    return this.origamiPost('/DeleteInboxEmails', body, null, null, true);
  }

  changeInboxEmailStatus(
    body: EmailServiceOptions.ChangeInboxEmailStatus,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ChangeInboxEmailStatus', body, null, silentErrorCodes || null, true);
  }

  exportEmails(body: EmailServiceOptions.ExportEmails): Observable<OrigamiResponse<{ JobId: string }>> {
    return this.origamiPost('/ExportEmails', body, null, null, true);
  }

  getMessageAttachment(
    siteId: number,
    messageId: number,
    attachmentId: number,
    forceRequest?: boolean,
    ...params
  ): Observable<OrigamiResponse<string>> {
    const paramAddition = 'locale=en'; // TODO: it's need get locale somewhere
    const body = {
      siteId,
      messageId,
      attachmentId,
      paramAddition,
    };
    return this.origamiPost('/GetMessageAttachment', body, params, null, forceRequest);
  }
}
